<template>
  <b-modal :visible="visible"
           title="Привязать к матчу BIGPLAY"
           size="lg"
           centered
           hide-footer
           @change="$emit('change', $event)"
           @show="reset">
    <div v-if="battle" class="mb mb-link-modal mb-stack">
      <div class="mb-alert mb-alert--info">
        <span>
          Игра <b>{{ battleTitle(battle) }}</b> не сопоставилась с нашим матчем автоматически.
          Выберите матч — команды получат наши названия и логотипы.
        </span>
      </div>

      <div class="mb-link-modal__steps">
        <div class="mb-field">
          <label for="mb-link-tournament">Турнир MLBB</label>
          <input id="mb-link-tournament" v-model.trim="search" class="mb-input" placeholder="Поиск по названию турнира">
        </div>
        <div class="mb-field">
          <label for="mb-link-stage">Этап</label>
          <select id="mb-link-stage" v-model="stageId" class="mb-select" :disabled="!tournamentId || loadingStages">
            <option :value="null">{{ loadingStages ? 'Загружаем…' : 'Выберите этап' }}</option>
            <option v-for="s in stages" :key="s.id" :value="s.id">{{ s.name || ('Этап #' + s.id) }}</option>
          </select>
        </div>
      </div>

      <div v-if="!tournamentId" class="mb-link-modal__list">
        <span v-if="loadingTournaments" class="mb-loading">Ищем турниры</span>
        <div v-else-if="!tournaments.length" class="mb-empty">Турниры не найдены</div>
        <button v-for="t in tournaments"
                v-else
                :key="t.id"
                type="button"
                class="mb-link-modal__item"
                @click="pickTournament(t)">
          <span class="mb-ellipsis">{{ t.name }}</span>
          <span class="mb-muted mb-small">#{{ t.id }}</span>
        </button>
      </div>

      <template v-else>
        <div class="mb-link-modal__picked">
          <span>Турнир: <b>{{ tournamentName }}</b></span>
          <button type="button" class="mb-link" @click="clearTournament">сменить</button>
        </div>

        <div v-if="stageId" class="mb-link-modal__list">
          <span v-if="loadingMatches" class="mb-loading">Загружаем матчи</span>
          <div v-else-if="!matches.length" class="mb-empty">В сетке этапа нет матчей с двумя командами</div>
          <div v-for="m in matches" v-else :key="m.group_id" :class="['mb-link-modal__item', {'is-suggested': m.suggested}]">
            <span class="mb-link-modal__match">
              <b class="mb-ellipsis">{{ m.title }}</b>
              <span class="mb-muted mb-small">
                #{{ m.group_id }}<template v-if="m.grid"> · {{ m.grid }}</template>
                <template v-if="m.suggested"> · совпадают названия</template>
              </span>
            </span>
            <button type="button" class="mb-btn mb-btn--sm" :disabled="linking" @click="link(m.group_id)">
              Привязать
            </button>
          </div>
        </div>
      </template>

      <form class="mb-link-modal__manual" @submit.prevent="linkManual">
        <div class="mb-field">
          <label for="mb-link-group">Или ID матча (group_id)</label>
          <input id="mb-link-group" v-model.trim="manualId" class="mb-input mb-mono" inputmode="numeric" placeholder="7781">
        </div>
        <button type="submit" class="mb-btn mb-btn--ghost" :disabled="linking || !manualId">Привязать по ID</button>
      </form>
    </div>
  </b-modal>
</template>

<script>
import api from '@/api/api'
import {MLBB_GAME_ID} from '@/constants/games'
import {battleTitle, campByNumber} from '@/common/mlbbBroadcast'

const SEARCH_DEBOUNCE_MS = 350

const normalize = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9а-яё]/gi, '')

export default {
  name: 'MbLinkMatchModal',
  model: {prop: 'visible', event: 'change'},
  props: {
    visible: {type: Boolean, default: false},
    battle: {type: Object, default: null},
  },
  data() {
    return {
      search: '',
      tournaments: [],
      loadingTournaments: false,
      tournamentId: null,
      tournamentName: '',
      stages: [],
      loadingStages: false,
      stageId: null,
      matches: [],
      loadingMatches: false,
      manualId: '',
      linking: false,
      searchTimer: null,
    }
  },
  watch: {
    search() {
      clearTimeout(this.searchTimer)
      this.searchTimer = setTimeout(this.loadTournaments, SEARCH_DEBOUNCE_MS)
    },
    stageId(id) {
      this.matches = []
      if (id) this.loadMatches(id)
    },
  },
  beforeDestroy() {
    clearTimeout(this.searchTimer)
  },
  methods: {
    battleTitle,

    reset() {
      this.search = ''
      this.manualId = ''
      this.clearTournament()
      this.loadTournaments()
    },

    clearTournament() {
      this.tournamentId = null
      this.tournamentName = ''
      this.stages = []
      this.stageId = null
      this.matches = []
    },

    async loadTournaments() {
      this.loadingTournaments = true
      try {
        const res = await api.tournament.index({
          'filter[game_id]': MLBB_GAME_ID,
          search: this.search || undefined,
          page: 1,
        })
        this.tournaments = res?.data?.tournaments || []
      } catch (e) {
        this.tournaments = []
      } finally {
        this.loadingTournaments = false
      }
    },

    async pickTournament(t) {
      this.tournamentId = t.id
      this.tournamentName = t.name
      this.loadingStages = true
      try {
        const res = await api.tournamentStage.tournamentStages(t.id)
        const data = res?.data
        this.stages = data?.stages || data?.data || (Array.isArray(data) ? data : [])
        if (this.stages.length === 1) this.stageId = this.stages[0].id
      } catch (e) {
        this.stages = []
      } finally {
        this.loadingStages = false
      }
    },

    async loadMatches(stageId) {
      this.loadingMatches = true
      try {
        const res = await api.grid.getGrid(stageId)
        if (stageId !== this.stageId) return
        this.matches = this.extractMatches(res?.data?.data?.grids || [])
      } catch (e) {
        this.matches = []
      } finally {
        this.loadingMatches = false
      }
    },

    extractMatches(grids) {
      const inGame = [1, 2].map((n) => normalize(campByNumber(this.battle, n)?.in_game_team_name)).filter(Boolean)
      const list = []
      grids.forEach((grid) => {
        const names = new Map((grid.teams || []).map((t) => [t.id, t.name]))
        ;(grid.matches || []).forEach((m) => {
          const teams = (m.teams || []).map((t) => names.get(t.id) || t.name).filter(Boolean)
          if (!m.group_id || teams.length < 2) return
          const norm = teams.map(normalize)
          // подсказка: игровое имя Moonton похоже на наше название (ONIC ↔ Onic Kazakhstan)
          const suggested = inGame.length > 0 && inGame.every((g) => norm.some((n) => n.includes(g) || g.includes(n)))
          list.push({group_id: m.group_id, title: teams.join(' — '), grid: grid.name || '', suggested})
        })
      })
      return list.sort((a, b) => Number(b.suggested) - Number(a.suggested))
    },

    linkManual() {
      const id = Number(this.manualId)
      if (!Number.isInteger(id) || id <= 0) return
      this.link(id)
    },

    async link(groupId) {
      if (!this.battle) return
      this.linking = true
      try {
        const res = await api.mlbbEsports.linkBattle(this.battle.id, groupId)
        this.$emit('linked', res?.data?.data || null)
        this.$emit('change', false)
      } catch (e) {
        // 422 «группы нет» и прочее покажет интерсептор
      } finally {
        this.linking = false
      }
    },
  },
}
</script>

<style lang="scss">
.mb-link-modal {
  &__steps {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }

  &__list {
    display: flex;
    flex-direction: column;
    max-height: 320px;
    overflow-y: auto;
    border: 1px solid var(--tl-border);
    border-radius: 10px;
  }

  &__item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    width: 100%;
    padding: 9px 12px;
    border: 0;
    border-bottom: 1px solid var(--tl-border);
    background: var(--tl-bg);
    color: var(--tl-text);
    font-size: 13px;
    text-align: left;

    &:last-child { border-bottom: 0; }
    &.is-suggested { background: var(--mb-ok-soft); }
  }

  button.mb-link-modal__item { cursor: pointer; }
  button.mb-link-modal__item:hover { background: var(--tl-soft); }

  &__match { display: flex; flex-direction: column; min-width: 0; }

  &__picked {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 13px;
  }

  &__manual {
    display: flex;
    align-items: flex-end;
    gap: 10px;
    padding-top: 12px;
    border-top: 1px solid var(--tl-border);

    .mb-field { flex: 0 1 220px; }
  }
}
</style>
